import { EntityRepository } from '@record-it-npm/synchro-client';
import { useCallback } from 'react';
import { EntityName } from 'record-it-time-common/EntityName';
import { EntityRepositoryByEntityName } from '../../../classes/entities/AppEntityManager';
import { AppEntityManagerEntityTypesByEntityName } from '../../../classes/entities/AppEntityManagerEntityTypesByEntityName';
import {
  Callback,
  useComputedEntityResult
} from '../useComputedEntityResult/useComputedEntityResult';

export function useEntity<
  TEntityName extends EntityName,
  TFunctionName extends RepositoryFunctionKeys<TEntityName>
>(
  entityName: TEntityName,
  functionName: TFunctionName,
  ...functionArgs: GetRepositoryFunctionParams<TEntityName, TFunctionName>
): GetRepositoryFunctionReturnType<TEntityName, TFunctionName> {
  const callback = useCallback<
    Callback<
      [TEntityName],
      GetRepositoryFunctionReturnType<TEntityName, TFunctionName>
    >
  >(
    ({ repositories }) => {
      return (repositories[0] as any)[functionName](...functionArgs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...functionArgs, functionName]
  );

  return useComputedEntityResult({
    entityNames: [entityName],
    callback
  });
}

type RepositoryFunctionKeys<TEntityName extends EntityName> = keyof {
  [key in Exclude<
    keyof EntityRepositoryByEntityName[TEntityName],
    RepositoryFunctionKeysToExclude
  > as IsValidRepositoryFunctionName<TEntityName, key>]: any;
};

type RepositoryFunctionKeysToExclude = Exclude<
  keyof EntityRepository<any, any, any>,
  'getById' | 'getByOriginalId'
>;

type IsValidRepositoryFunctionName<
  TEntityName extends EntityName,
  TFunctionName extends keyof EntityRepositoryByEntityName[TEntityName]
> = EntityRepositoryByEntityName[TEntityName][TFunctionName] extends (
  ...args: Array<any>
) => AppEntityManagerEntityTypesByEntityName[TEntityName]['entity'] | null
  ? TFunctionName
  : never;

type GetRepositoryFunctionParams<
  TEntityName extends EntityName,
  TFunctionName extends RepositoryFunctionKeys<TEntityName>
> = EntityRepositoryByEntityName[TEntityName][TFunctionName] extends (
  ...args: Array<any>
) => any
  ? Parameters<EntityRepositoryByEntityName[TEntityName][TFunctionName]>
  : never;

type GetRepositoryFunctionReturnType<
  TEntityName extends EntityName,
  TFunctionName extends RepositoryFunctionKeys<TEntityName>
> = EntityRepositoryByEntityName[TEntityName][TFunctionName] extends (
  ...args: Array<any>
) => any
  ? ReturnType<EntityRepositoryByEntityName[TEntityName][TFunctionName]>
  : never;
